// Capacitor push-notifications helper.
//
// On Android, `@capacitor/push-notifications` emits the FCM registration
// token directly via the `registration` event — the Firebase Android SDK
// owns the lifecycle and the plugin just forwards it.
//
// On iOS, the SAME plugin emits the **APNs device token** instead — that's
// useless for FCM v1 send. To get the actual FCM registration token we
// listen for a `fcmTokenReceived` CustomEvent dispatched from native code
// (AppDelegate.swift implements `MessagingDelegate` and forwards the FCM
// token via `webView.evaluateJavaScript`). See patch_appdelegate_firebase()
// in /app/scripts/build_app.py.
//
// The plugin (@capacitor/push-notifications) is added to the Capacitor build
// at scaffolding time — it is NOT installed as an npm dep in this React PWA.
// At runtime inside the native WebView, the plugin is exposed as
// `window.Capacitor.Plugins.PushNotifications`. On regular web browsers
// `window.Capacitor` is undefined and every call becomes a no-op.
import api from "./api";

// The token + persona this device most recently registered. Needed at logout:
// the row in `device_tokens` is keyed by token, so releasing it requires
// knowing which one we claimed.
let lastRegistration = null;

function getPlugin() {
  const cap = (typeof window !== "undefined") && window.Capacitor;
  if (!cap || !cap.isNativePlatform || !cap.isNativePlatform()) return null;
  return cap.Plugins?.PushNotifications || null;
}

/**
 * Which APP this device is running — distinct from `platform` (ios/android)
 * and from `persona` (the role the user logged in as).
 *
 * A rider may hold BOTH the dedicated Rider App and the Customer App (which
 * also offers rider login). Those are two installs, two FCM tokens, and both
 * must receive rider notifications — so the two records have to be
 * distinguishable, and `persona` alone can't do it: both would say "rider".
 *
 * Derived from the native bundle id, whose brand segment is fixed per app by
 * build_app.PERSONA_SEGMENT:
 *   in.localappstore.dairyfresh.<slug>       → customer_app
 *   in.localappstore.dairyfreshrider.<slug>  → rider_app
 *   in.localappstore.dairyfreshadmin.<slug>  → admin_app
 *
 * Returns "web" off-native, and null when the id can't be read — the field
 * is optional server-side, so an unknown app never blocks registration.
 */
export async function detectApp() {
  const cap = (typeof window !== "undefined") && window.Capacitor;
  if (!cap || !cap.isNativePlatform || !cap.isNativePlatform()) return "web";
  try {
    const info = await cap.Plugins?.App?.getInfo?.();
    const id = (info?.id || "").toLowerCase();
    if (!id) return null;
    // Order matters: "dairyfreshrider" and "dairyfreshadmin" both contain
    // "dairyfresh", so the more specific segments are tested first.
    if (id.includes(".dairyfreshrider.")) return "rider_app";
    if (id.includes(".dairyfreshadmin.")) return "admin_app";
    if (id.includes(".dairyfresh.")) return "customer_app";
    return null;
  } catch {
    return null;
  }
}

function getPlatform() {
  const cap = (typeof window !== "undefined") && window.Capacitor;
  return cap?.getPlatform ? cap.getPlatform() : "web";
}

/**
 * Ask the OS for permission and register the device with FCM. On success the
 * device token is POSTed to /api/push/register.
 *
 * Diagnostic events are dispatched on `window` so a debug UI can pick them up:
 *   - "push-debug" with { stage, detail }
 *
 * @param {"customer"|"rider"|"admin"} persona
 * @returns {Promise<{registered:boolean, reason?:string, token?:string}>}
 */
export async function registerForPush(persona) {
  const plugin = getPlugin();
  const platform = getPlatform();
  const emit = (stage, detail) => {
    try {
      window.dispatchEvent(new CustomEvent("push-debug", { detail: { stage, detail } }));
    } catch { /* ignore */ }
    if (typeof console !== "undefined") console.log(`[push:${stage}]`, detail);
  };

  if (!plugin) {
    emit("not-native", { platform });
    return { registered: false, reason: "not-native" };
  }

  try {
    let perm = await plugin.checkPermissions();
    emit("permissions-check", perm);
    if (perm.receive !== "granted") {
      perm = await plugin.requestPermissions();
      emit("permissions-requested", perm);
    }
    if (perm.receive !== "granted") {
      return { registered: false, reason: "permission-denied" };
    }

    // Wire listeners ONCE — re-calling registerForPush won't double-bind because
    // we remove any previous listeners first.
    await plugin.removeAllListeners();

    // --- Foreground delivery ---
    // When the app is in the FOREGROUND, Android/iOS do NOT draw a tray
    // notification: the plugin hands it to JS instead, and if nothing listens
    // it is silently dropped. That made every push look broken to anyone
    // testing with the app open. Re-broadcast as a window event so the app
    // shell can surface it (see PushToasts).
    plugin.addListener("pushNotificationReceived", (notif) => {
      emit("foreground-received", { title: notif?.title });
      try {
        window.dispatchEvent(new CustomEvent("push:received", { detail: notif }));
      } catch { /* ignore */ }
    });

    // --- Tap handling ---
    // The backend has always sent a `deep_link` in the data payload, but
    // nothing consumed it, so tapping a notification just opened the app on
    // whatever screen it was last on.
    plugin.addListener("pushNotificationActionPerformed", (action) => {
      const link = action?.notification?.data?.deep_link;
      emit("notification-tapped", { deep_link: link });
      if (!link) return;
      try {
        window.dispatchEvent(new CustomEvent("push:navigate", { detail: { deep_link: link } }));
      } catch { /* ignore */ }
    });

    const postToBackend = async (token, source) => {
      emit("posting-to-backend", { source, token_tail: token.slice(-10), platform });
      try {
        const res = await api.post("/api/push/register", {
          token,
          platform,
          persona,
          // Which app this install is, so a rider holding BOTH the Rider App
          // and the Customer App has two distinguishable token records.
          app: await detectApp(),
          app_version: (typeof window !== "undefined" && window.__APP_VERSION__) || null,
        });
        emit("backend-success", { source, response: res.data });
        lastRegistration = { token, platform, persona };
        return token;
      } catch (err) {
        emit("backend-error", {
          source,
          status: err?.response?.status,
          data: err?.response?.data,
          message: err?.message,
        });
        // Resolve anyway so the hook doesn't hang; surface for diagnosis.
        return token;
      }
    };

    const tokenPromise = new Promise((resolve, reject) => {
      // --- iOS path: FCM token comes from native via CustomEvent ---
      if (platform === "ios") {
        // If the token already arrived before the hook mounted (race), pick it up.
        if (typeof window !== "undefined" && window.__fcmToken) {
          emit("fcm-token-cached", { token_tail: window.__fcmToken.slice(-10) });
          postToBackend(window.__fcmToken, "ios-cached").then(resolve);
          return;
        }
        const onFcm = (e) => {
          const token = e?.detail || (typeof window !== "undefined" && window.__fcmToken);
          if (!token) return;
          window.removeEventListener("fcmTokenReceived", onFcm);
          emit("fcm-token-received", { token_tail: token.slice(-10) });
          postToBackend(token, "ios-native").then(resolve).catch(reject);
        };
        window.addEventListener("fcmTokenReceived", onFcm);
      }

      // --- Android path: FCM token arrives via Capacitor plugin event ---
      plugin.addListener("registration", async (info) => {
        const token = info?.value || info?.token;
        if (!token) return reject(new Error("empty-token"));
        emit("plugin-registration", { token_tail: token.slice(-10) });
        // On iOS this is the APNs token (useless for FCM v1) — ignore here;
        // the MessagingDelegate path above will deliver the real FCM token.
        if (platform === "ios") {
          emit("plugin-registration-ignored-on-ios", { reason: "apns-token-not-fcm" });
          return;
        }
        postToBackend(token, "plugin").then(resolve).catch(reject);
      });
      plugin.addListener("registrationError", (e) => {
        emit("registration-error", e);
        reject(new Error(e?.error || "registration-error"));
      });
    });

    await plugin.register();
    emit("plugin-register-called", { platform });
    const token = await Promise.race([
      tokenPromise,
      new Promise((_, rej) => setTimeout(() => rej(new Error("register-timeout")), 30000)),
    ]);
    return { registered: true, token };
  } catch (err) {
    emit("error", { message: err?.message });
    return { registered: false, reason: err?.message || "error" };
  }
}

/**
 * Release this device's push token on logout.
 *
 * `device_tokens` rows are keyed by FCM token — one row per device — so a row
 * left behind after logout still points at the PREVIOUS user. The next person
 * to sign in on that device then receives the previous user's notifications
 * until (and unless) their own screen re-registers. With one app serving all
 * three roles on shared/handover devices, that is a real privacy leak, not a
 * theoretical one.
 *
 * MUST be awaited before the session is cleared — the endpoint is
 * authenticated, and it deletes only a row belonging to the caller.
 * Best-effort: a failure here must never block logout.
 */
export async function unregisterForPush() {
  const reg = lastRegistration;
  if (!reg) return { unregistered: false, reason: "nothing-registered" };
  try {
    await api.post("/api/push/unregister", reg);
    lastRegistration = null;
    return { unregistered: true };
  } catch (err) {
    return { unregistered: false, reason: err?.message || "error" };
  }
}

/**
 * Map a logged-in user's role to the push persona for their device.
 *
 * There is ONE app — the customer build also hosts admin and rider login —
 * so a device holds a single FCM token and the persona must follow WHO is
 * logged in, not which screen happened to mount. Deriving it from the role
 * in one place stops the value ping-ponging as a user moves between the
 * customer and admin sections of the same app, which previously relabelled
 * their only device and made them unreachable.
 *
 * Returns null for an unknown/absent role so callers can skip registering
 * rather than guess.
 */
export function personaForRole(role) {
  if (role === "customer") return "customer";
  if (role === "delivery_boy") return "rider";
  if (role === "tenant_admin" || role === "supervisor") return "admin";
  return null;
}

/** True only when running inside a Capacitor native shell. */
export function isNativeApp() {
  return !!getPlugin();
}
